import type { Content as MdastNode } from 'mdast';

import type { State } from '../state';

type UnknownMdastNode = MdastNode & {
	alt?: unknown;
	children?: unknown;
	identifier?: unknown;
	label?: unknown;
	position?: {
		end?: { offset?: unknown };
		start?: { offset?: unknown };
	};
	type: string;
	url?: unknown;
	value?: unknown;
};

const valueToText = (value: unknown): string => (typeof value === 'string' ? value : '');

const getSourceMarkdownSlice = (
	node: UnknownMdastNode,
	sourceMarkdown: string | undefined,
): string => {
	if (!sourceMarkdown) {
		return '';
	}

	const startOffset = node.position?.start?.offset;
	const endOffset = node.position?.end?.offset;
	if (
		typeof startOffset !== 'number' ||
		typeof endOffset !== 'number' ||
		startOffset < 0 ||
		endOffset <= startOffset ||
		endOffset > sourceMarkdown.length
	) {
		return '';
	}

	return sourceMarkdown.slice(startOffset, endOffset);
};

const childrenToText = (node: UnknownMdastNode, sourceMarkdown: string | undefined): string => {
	if (!Array.isArray(node.children)) {
		return '';
	}

	const separator = node.type === 'paragraph' || node.type === 'heading' ? '' : '\n';
	return node.children
		.map((child) => getUnsupportedMarkdownText(child as MdastNode, sourceMarkdown))
		.filter(Boolean)
		.join(separator);
};

const getUnsupportedMarkdownText = (node: MdastNode, sourceMarkdown?: string): string => {
	const unknownNode = node as UnknownMdastNode;
	const sourceText = getSourceMarkdownSlice(unknownNode, sourceMarkdown);
	if (sourceText) {
		return sourceText;
	}

	const directText =
		valueToText(unknownNode.value) ||
		valueToText(unknownNode.alt) ||
		valueToText(unknownNode.label) ||
		valueToText(unknownNode.identifier) ||
		valueToText(unknownNode.url);

	if (directText) {
		return directText;
	}

	const childText = childrenToText(unknownNode, sourceMarkdown);

	return childText;
};

export function unsupportedMarkdownContent(state: State, node: MdastNode): void {
	const text = getUnsupportedMarkdownText(node, state.sourceMarkdown);
	if (!text) {
		return;
	}

	const top = state.top();
	const { paragraph } = state.schema.nodes;

	if (!top.type.inlineContent && paragraph && top.type.contentMatch.matchType(paragraph)) {
		state.openNode(paragraph);
		state.addText(text);
		state.closeNode();
		return;
	}

	state.addText(text);
}
